import React, { useEffect, useState } from "react"
import { TableHead, TableBody, TableRow, Stack } from "@mui/material"
import { StyledTable, StyledHeaderCell, StyledBodyCell, StyledFooterCell, } from "../../../components/styled/StyledTableComponents"
import Typography from "@mui/material/Typography"
import { COLORS } from "../../../utils"
import { convertKilosIntoGrams } from "../../../utils/ressources"
import { roundNumber } from "../../../utils"
import { getPseWithSectionOutputs } from "../../../parseManager/productionStepExecutions/parseProductionStepExecutionManager"

const _toGramsInt = (kg) => roundNumber(convertKilosIntoGrams(kg || 0), 1)

const resolveUnitForSection = (section) => {
  const hasPiece = section?.section?.steps?.some(
    st => (st.ingredients || []).some(ing => ing.servingUnit === "piece")
  )
  return hasPiece ? "piece" : "weight"
}

const formatCell = (left, right, unit) => {
  if (!left && !right) return "-"
  const unitLabel = unit === "piece" ? "pièce" : unit === "g" ? "g" : "kg"
  return (
    <Stack direction="row" alignItems="center" gap={1}>
      {left != null ? left : "-"}{right != null ? <> / {right}</> : null}
      <span style={{ color: "#7C7C7C" }}>{unitLabel}</span>
    </Stack>
  )
}

const PackagingExecutionSectionsResume = ({ sections = [] }) => {
  const [localSections, setLocalSections] = useState(sections)

  useEffect(() => {
    (async () => {
      // Keep only unused sectionOutputs if exist
      const currentSections = await Promise.all(sections.map(async section => {
        const outputs = section.section?.sectionOutputs
        if (!outputs) return section
        
        const outputPercentages = await Promise.all(outputs.map(async output => {
          const pse = await getPseWithSectionOutputs([output])
          if (pse) return { percentage: 0, name: output.name ?? `${output.percentage} %` }
          const totalNetWeight = outputs.reduce((acc, output) => acc + (output.ingredients ?? []).reduce((acc, ingredient) => acc + ingredient.netWeight, 0), 0)
          return {
            percentage: output.percentage ? output.percentage : (output.ingredients.reduce((acc, ingredient) => acc + ingredient.netWeight, 0) / totalNetWeight * 100),
            name: output.name ?? `${output.percentage} %`
          }
        })).then(percentages => {
          const totalPercentage = percentages.reduce((acc, output) => acc + output.percentage, 0)
          const names = percentages.filter(output => output.percentage).map(output => output.name).join(", ")
          return {
            outputPercentages: percentages,
            totalPercentage,
            names
          }
        })

        if (!outputPercentages.totalPercentage) return section

        return {
          ...section,
          ...["recipeSectionWeight", "realWeight", "forecastWaste", "realWaste"].reduce((acc, key) => {
            if (section[key])
              acc[key] = section[key] * outputPercentages.totalPercentage / 100
            return acc
          }, {}),
          sectionName: section.sectionName + (outputPercentages.totalPercentage ? " - " + outputPercentages.names : ""),
        }
      }))
      setLocalSections(currentSections)
    })()
  }, [sections])

  const totalRecipeWeightKg = localSections.reduce((acc, s) => acc + (s.recipeSectionWeight || 0), 0)
  const totalRealWeightKg = localSections.reduce((acc, s) => acc + (s.realWeight || 0), 0)
  const totalForecastWasteKg = localSections.reduce((acc, s) => acc + (s.forecastWaste || 0), 0)
  const totalRealWasteKg = localSections.reduce((acc, s) => acc + (s.realWaste || 0), 0)
  const labels = ["Éléments à barquetter", "Poids fiche recette / réel barquetté", "Restes prévus / réels"]

  return (
    <Stack spacing={2} sx={{ width: "100%" }}>
      <Stack spacing={1} direction="row">
        <img
          src="/img/packagingExecutions/distributionBySections.svg"
          alt="Packaging Icon"
          style={{ width: 24, height: 24, padding: 2, color: COLORS.BLACK800 }}
        />
        <Typography
          variant="h6"
          sx={{ fontSize: 16, lineHeight: 1.5, color: COLORS.BLACK800 }}
        >
          Répartition par sections
        </Typography>
      </Stack>
      <StyledTable>
        <TableHead>
          <TableRow>
            {labels.map(label => (<StyledHeaderCell key={label}>{label}</StyledHeaderCell>))}
          </TableRow>
        </TableHead>

        <TableBody>
          {localSections
          .filter(s => (s.recipeSectionWeight || 0) > 0.001)
          .map((s, idx) => {
            const unitKind = resolveUnitForSection(s)
            const unitForWeight = unitKind === "piece" ? "piece" : "g"

            const recipeVal = unitKind === "piece" ? (s.recipeSectionWeight ?? 0) : _toGramsInt(s.recipeSectionWeight)
            const realVal = unitKind === "piece" ? (s.realWeight ?? 0) : _toGramsInt(s.realWeight)

            const forecastWasteVal = roundNumber(s.forecastWaste || 0, 2)
            const realWasteVal = roundNumber(s.realWaste || 0, 2)

            return (
              <TableRow key={idx}>
                <StyledBodyCell leftEdge={true} lastBodyRow={idx === localSections.length - 1}>{s.sectionName}</StyledBodyCell>
                <StyledBodyCell lastBodyRow={idx === localSections.length - 1}>{formatCell(recipeVal, realVal, unitForWeight)}</StyledBodyCell>
                <StyledBodyCell rightEdge={true} lastBodyRow={idx === localSections.length - 1}>{formatCell(forecastWasteVal, realWasteVal, "kg")}</StyledBodyCell>
              </TableRow>
            )
          })}

          <TableRow>
            <StyledFooterCell>Total</StyledFooterCell>
            <StyledFooterCell>
              {formatCell(_toGramsInt(totalRecipeWeightKg), _toGramsInt(totalRealWeightKg), "g")}
            </StyledFooterCell>
            <StyledFooterCell>
              {formatCell(roundNumber(totalForecastWasteKg, 1), roundNumber(totalRealWasteKg, 1), "kg")}
            </StyledFooterCell>
          </TableRow>
        </TableBody>
      </StyledTable>
    </Stack>
  )
}

export default PackagingExecutionSectionsResume